import React from 'react';
import { PillArrow } from './Logo.jsx';
import { firstEntryId } from './tree.js';

/**
 * The overview, laid out like edl.dk's hero: a large statement, an intro
 * under it on the left, a pill to start, then the numbers that matter.
 */
export function Home({ config, index, tree, onNavigate, onSearch }) {
  const entries = index.order.map((id) => index.entries[id]);
  const stories = entries.filter((e) => e.type === 'story');
  const components = new Set(stories.map((e) => e.componentId)).size;
  const pages = entries.filter((e) => e.type === 'docs' && e.importPath).length;
  const first = entries.find((e) => e.type === 'docs') ?? entries[0];
  const sections = tree.children.filter((s) => s.name);

  return (
    <div className="home">
      <section className="home-hero">
        <h1 className="home-hero__title">{config.title}</h1>
        <div className="home-hero__row">
          <div className="home-hero__intro">
            <p>
              Every component, its states and how to use it, rendered from the same source the product ships.
            </p>
            <div className="home-hero__actions">
              {first && (
                <button type="button" className="edl-pill" onClick={() => onNavigate(first.id)}>
                  Start reading
                  <span className="edl-pill__disc">
                    <PillArrow />
                  </span>
                </button>
              )}
              <button type="button" className="edl-pill edl-pill--ghost" onClick={onSearch}>
                Search
                <span className="edl-pill__disc">
                  <kbd className="home-kbd">⌘K</kbd>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="home-stats" aria-label="Library in numbers">
        <Stat value={components} label="components" />
        <Stat value={stories.length} label="stories" />
        <Stat value={pages} label="written pages" />
        {index.errors?.length > 0 && <Stat value={index.errors.length} label="files skipped" note={index.errors.map((e) => `${e.file}: ${e.message}`).join('\n')} />}
      </section>

      {sections.length > 0 && (
        <section className="home-sections">
          {sections.map((section) => {
            const count = countComponents(section);
            const target = firstEntryId(section);
            return (
              <button key={section.key} type="button" className="home-section edl-disc-trigger" onClick={() => target && onNavigate(target)}>
                <span className="home-section__name">{section.name}</span>
                <span className="home-section__count">{count}</span>
                <span className="edl-pill__disc edl-pill__disc--inverse home-section__disc" aria-hidden="true">
                  <PillArrow />
                </span>
              </button>
            );
          })}
        </section>
      )}
    </div>
  );
}

function Stat({ value, label, note }) {
  return (
    <div className="home-stat" title={note}>
      <p className="home-stat__value">{value}</p>
      <p className="home-stat__label">{label}</p>
    </div>
  );
}

function countComponents(node) {
  if (node.type === 'component') return 1;
  return node.children.reduce((n, c) => n + countComponents(c), 0);
}
